import React from 'react';
import {
  Box, Checkbox, Chip, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Typography,
} from '@mui/material';
import { Code, EmptyState, StatusChip } from 'dashboard/components/common';
import { fmtDateTime, fmtMoney } from 'utilities/format';

const STATUS_COLORS = {
  pending: 'warning',
  paid: 'success',
  fulfilled: 'info',
  cancelled: 'default',
  refunded: 'error',
};

/**
 * Orders table — head + body. Owns the row rendering and click-to-open
 * behaviour; the parent owns selection state, the active order, and
 * the open/close dialog.
 *
 * Clicking a row opens the order detail dialog (via `onRowClick`);
 * clicking the checkbox cell short-circuits so toggling selection
 * doesn't also open the modal.
 */
export default function OrderTable({
  rows, loading,
  selected, onToggleRow,
  allSelected, someSelected, onToggleAll,
  onRowClick,
}) {
  return (
    <TableContainer
      component={Box}
      sx={{ border: '1px solid', borderColor: 'divider', borderRadius: '12px', overflowX: 'auto' }}
    >
      <Table size='small'>
        <TableHead sx={{ bgcolor: 'action.hover' }}>
          <TableRow>
            <TableCell width='42px' padding='checkbox'>
              <Checkbox
                size='small'
                checked={allSelected}
                indeterminate={someSelected}
                onChange={onToggleAll}
                inputProps={{ 'aria-label': 'Select all on this page' }}
              />
            </TableCell>
            <TableCell>Number</TableCell>
            <TableCell>Placed</TableCell>
            <TableCell>Customer</TableCell>
            <TableCell>Items</TableCell>
            <TableCell>Status</TableCell>
            <TableCell align='right'>Total</TableCell>
            <TableCell>Source</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {loading && rows.length === 0 && (
            <TableRow><TableCell colSpan={8}><EmptyState>Loading…</EmptyState></TableCell></TableRow>
          )}
          {!loading && rows.length === 0 && (
            <TableRow><TableCell colSpan={8}><EmptyState>No orders yet.</EmptyState></TableCell></TableRow>
          )}
          {rows.map((o) => {
            const isChecked = selected.has(String(o._id));
            return (
              <TableRow
                key={o._id}
                hover
                sx={{ cursor: 'pointer', bgcolor: isChecked ? 'action.selected' : undefined }}
                onClick={(e) => {
                  if (e.target?.tagName === 'INPUT') return;
                  onRowClick(o);
                }}
              >
                <TableCell padding='checkbox' onClick={(e) => e.stopPropagation()}>
                  <Checkbox
                    size='small'
                    checked={isChecked}
                    onChange={() => onToggleRow(o._id)}
                    inputProps={{ 'aria-label': `Select order ${o.number || ''}` }}
                  />
                </TableCell>
                <TableCell><Code>{o.number || '—'}</Code></TableCell>
                <TableCell>
                  <Typography variant='caption'>{fmtDateTime(o.placed_at)}</Typography>
                </TableCell>
                <TableCell>
                  <Typography variant='body2'>{o.shipping_address?.name || '—'}</Typography>
                  <Typography variant='caption' color='text.secondary'>
                    {o.shipping_address?.email || ''}
                  </Typography>
                </TableCell>
                <TableCell>{(o.line_items || []).length}</TableCell>
                <TableCell><StatusChip status={o.status} colors={STATUS_COLORS} /></TableCell>
                <TableCell align='right'>
                  <Typography variant='body2' fontWeight='600'>
                    {fmtMoney(o.grand_total, o.currency)}
                  </Typography>
                </TableCell>
                <TableCell><Chip size='small' label={o.source} /></TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
}
